// TODO move to tw-api-js
import type { FormDataAdapter } from "$lib/util"
import {
  USA_CITIZENSHIP_TYPES,
  USA_COUNTRY_CODE,
  VISA_TYPES,
  convertDateToISOString,
  parseISODateString,
  type Customer,
} from "@tastyworks/tastyworks-api"
import {
  dateISOString,
  isFutureDate,
  isWithinMaxYears,
} from "@tastyworks/tastyworks-api/validate"
import type { PartialDeep } from "type-fest"
import { boolean, object, string, type InferType } from "yup"

export const citizenshipCountrySchema = object({
  citizenshipCountry: string().required(),
  birthCountry: string().when(
    "citizenshipCountry",
    ([citizenshipCountry], schema) =>
      citizenshipCountry === USA_COUNTRY_CODE
        ? schema.notRequired()
        : schema.required()
  ),
  isForeigner: boolean().test({
    name: "is-expat",
    test: (isForeigner, { parent }) => {
      return !(isForeigner && parent.citizenshipCountry === USA_COUNTRY_CODE)
    },
  }),
})

export const isPermanentResidentSchema = object({
  isPermanentResident: boolean().required(),
})

export const visaSchema = object({
  visaType: string().oneOf(Object.values(VISA_TYPES)).required(),
  visaExpirationDate: dateISOString()
    .required()
    .test(isFutureDate())
    .test(isWithinMaxYears(20)),
})

export const citizenshipStatusSchema = object({
  citizenship: citizenshipCountrySchema,
  residency: isPermanentResidentSchema,
  visa: visaSchema,
})

export const citizenshipAdapter: FormDataAdapter<
  Customer,
  InferType<typeof citizenshipCountrySchema>
> = {
  toForm: (customer, form) => {
    form.citizenshipCountry = customer.citizenshipCountry
    form.birthCountry = customer.birthCountry
    form.isForeigner = customer.isForeign
    return form
  },

  toModel: (form, customer) => {
    customer.citizenshipCountry = form.citizenshipCountry
    customer.birthCountry = form.birthCountry ?? ""
    return customer
  },
}

export const residencyAdapter: FormDataAdapter<
  Customer,
  InferType<typeof isPermanentResidentSchema>
> = {
  toForm: (customer, form) => {
    form.isPermanentResident = customer.isPermanentResident

    return form
  },
  toModel: (_, customer) => {
    // no-op
    return customer
  },
}

export const visaAdapter: FormDataAdapter<
  Customer,
  InferType<typeof visaSchema>
> = {
  toForm: (customer, form) => {
    form.visaType = customer.visaType ?? undefined
    form.visaExpirationDate = convertDateToISOString(
      customer.visaExpirationDate
    )

    return form
  },
  toModel: (form, customer) => {
    customer.visaType = form.visaType
    customer.visaExpirationDate = parseISODateString(form.visaExpirationDate)

    return customer
  },
}

type CitizenshipStatusData = InferType<typeof citizenshipStatusSchema>

export function deriveUSCitizenshipType(
  citizenshipStatus: PartialDeep<CitizenshipStatusData>
) {
  // NOTE: US citizens living abroad (isForeigner) cannot apply.
  if (citizenshipStatus.citizenship?.isForeigner) {
    return USA_CITIZENSHIP_TYPES.NONE
  }

  return citizenshipStatus.citizenship?.citizenshipCountry === USA_COUNTRY_CODE
    ? USA_CITIZENSHIP_TYPES.CITIZEN
    : citizenshipStatus.residency?.isPermanentResident
      ? USA_CITIZENSHIP_TYPES.PERMANENT_RESIDENT
      : USA_CITIZENSHIP_TYPES.NONRESIDENT_ALIEN
}

export const citizenshipTypeAdapter: FormDataAdapter<
  Customer,
  CitizenshipStatusData
> = {
  toForm: (_, form) => {
    // no-op
    return form
  },
  toModel: (form, customer) => {
    customer.usaCitizenshipType = deriveUSCitizenshipType(form)
    return customer
  },
}
